import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import * as pdfjs from "pdfjs-dist";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

import { BrandedLoader } from "../components/BrandedLoader";
import { cn } from "../lib/utils";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

type PdfDocumentViewerProps = {
  filename: string;
  url: string;
};

type PdfDocument = Awaited<ReturnType<typeof pdfjs.getDocument>["promise"]>;
type PdfPage = Awaited<ReturnType<PdfDocument["getPage"]>>;

export function PdfDocumentViewer({ filename, url }: PdfDocumentViewerProps) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [document, setDocument] = useState<PdfDocument | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [containerWidth, setContainerWidth] = useState(0);
  const [status, setStatus] = useState<"error" | "loading" | "ready">("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let canceled = false;
    const loadingTask = pdfjs.getDocument({ url, withCredentials: false });

    setStatus("loading");
    setError(null);
    setDocument(null);
    setPageNumber(1);
    setPageCount(0);

    loadingTask.promise
      .then((loadedDocument) => {
        if (canceled) {
          void loadingTask.destroy();
          return;
        }

        setDocument(loadedDocument);
        setPageCount(loadedDocument.numPages);
        setStatus("ready");
      })
      .catch(() => {
        if (!canceled) {
          setStatus("error");
          setError("MedoPilot could not render this PDF.");
        }
      });

    return () => {
      canceled = true;
      void loadingTask.destroy();
    };
  }, [url]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return undefined;
    }

    const updateWidth = () => {
      setContainerWidth(Math.max(0, Math.floor(viewport.clientWidth)));
    };

    updateWidth();
    const resizeObserver = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateWidth);
    resizeObserver?.observe(viewport);
    window.addEventListener("resize", updateWidth);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateWidth);
    };
  }, [status]);

  useEffect(() => {
    if (!document || !canvasRef.current || containerWidth <= 0) {
      return;
    }

    let canceled = false;
    let renderTask: { cancel: () => void; promise: Promise<unknown> } | null = null;

    async function renderPage(activeDocument: PdfDocument) {
      try {
        const page: PdfPage = await activeDocument.getPage(pageNumber);
        if (canceled || !canvasRef.current) {
          return;
        }

        const naturalViewport = page.getViewport({ scale: 1 });
        const fitScale = Math.min(1, Math.max(0.1, (containerWidth - 8) / naturalViewport.width));
        const viewport = page.getViewport({ scale: fitScale * zoom });
        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");
        if (!context) {
          throw new Error("Canvas is not available.");
        }

        const pixelRatio = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * pixelRatio);
        canvas.height = Math.floor(viewport.height * pixelRatio);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

        renderTask = page.render({ canvas, canvasContext: context, viewport });
        await renderTask.promise;
      } catch (nextError) {
        if (!canceled && !(nextError instanceof Error && nextError.name === "RenderingCancelledException")) {
          setStatus("error");
          setError("MedoPilot could not render this PDF page.");
        }
      }
    }

    void renderPage(document);

    return () => {
      canceled = true;
      renderTask?.cancel();
    };
  }, [containerWidth, document, pageNumber, zoom]);

  if (status === "loading") {
    return (
      <div className="px-4 py-8">
        <BrandedLoader compact label="Rendering PDF" />
      </div>
    );
  }

  if (status === "error") {
    return <p className="px-4 py-5 text-sm font-semibold text-destructive">{error ?? "MedoPilot could not render this PDF."}</p>;
  }

  return (
    <div className="flex h-[calc(100dvh-13rem)] min-h-[24rem] flex-col bg-slate-950/5 lg:h-[72vh] lg:min-h-[28rem]">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-white px-3 py-2">
        <div className="text-xs font-medium text-muted-foreground" aria-live="polite">
          Page {pageNumber} of {pageCount}
        </div>
        <div className="flex items-center gap-1" aria-label="PDF controls">
          <PdfViewerButton
            label="Previous Page"
            onClick={() => setPageNumber((current) => Math.max(1, current - 1))}
            disabled={pageNumber <= 1}
          >
            <ChevronLeft className="size-4" aria-hidden="true" />
          </PdfViewerButton>
          <PdfViewerButton
            label="Next Page"
            onClick={() => setPageNumber((current) => Math.min(pageCount, current + 1))}
            disabled={pageNumber >= pageCount}
          >
            <ChevronRight className="size-4" aria-hidden="true" />
          </PdfViewerButton>
          <span className="mx-1 h-5 w-px bg-border" aria-hidden="true" />
          <PdfViewerButton label="Zoom Out" onClick={() => setZoom((current) => Math.max(0.75, current - 0.25))} disabled={zoom <= 0.75}>
            <ZoomOut className="size-4" aria-hidden="true" />
          </PdfViewerButton>
          <span className="min-w-12 text-center text-xs font-medium text-muted-foreground">{Math.round(zoom * 100)}%</span>
          <PdfViewerButton label="Zoom In" onClick={() => setZoom((current) => Math.min(2, current + 0.25))} disabled={zoom >= 2}>
            <ZoomIn className="size-4" aria-hidden="true" />
          </PdfViewerButton>
        </div>
      </div>
      <div ref={viewportRef} className="flex-1 overflow-auto px-2 py-3 sm:px-5 sm:py-4" role="region" aria-label={`PDF preview for ${filename}`}>
        <canvas ref={canvasRef} className="mx-auto block max-w-none rounded-md bg-white shadow-soft" aria-label={`Rendered PDF page for ${filename}`} />
      </div>
    </div>
  );
}

function PdfViewerButton({
  children,
  disabled,
  label,
  onClick,
}: {
  children: ReactNode;
  disabled?: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex size-9 items-center justify-center rounded-full border border-border bg-white text-foreground transition focus:outline-none focus:ring-4 focus:ring-primary/10",
        disabled ? "cursor-not-allowed opacity-40" : "hover:border-primary/30 hover:text-primary",
      )}
    >
      {children}
    </button>
  );
}
